import { useCallback, useEffect, useState } from "react";
import { normalizeManifest } from "@/lib/visitManifest";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isVisitRowId(id) {
  return UUID_RE.test(String(id || ""));
}

function tokenKey(visitId) {
  return `bricks.visit.token.${visitId}`;
}

function readToken(visitId) {
  try {
    return window.sessionStorage.getItem(tokenKey(visitId)) || "";
  } catch {
    return "";
  }
}

/**
 * Load a Visit manifest from the OS public API.
 * state: idle | loading | ready | locked | missing | error
 * When visitId is not a property_visits row (synthesized delivery visit),
 * falls back to a one-room manifest from fileUrl without any request.
 */
export function useVisitManifest({ visitId, fileUrl = null, unbranded = false, enabled = true }) {
  const [state, setState] = useState("idle");
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState("");
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!isVisitRowId(visitId)) {
      const m = fileUrl ? normalizeManifest({ file_url: fileUrl, unbranded }) : null;
      setManifest(m);
      setState(m ? "ready" : "missing");
      return undefined;
    }
    let cancelled = false;
    setState("loading");
    const token = readToken(visitId);
    const qs = unbranded ? "?unbranded=1" : "";
    fetch(`${BASE}/api/public/visits/${encodeURIComponent(visitId)}${qs}`, {
      headers: token ? { "X-Visit-Token": token } : {},
    })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (r.status === 401 && body && (body.password_required || body.detail?.password_required)) {
          setState("locked");
          setManifest(normalizeManifest({ ...(body.detail || body), rooms: [], file_url: null }) || null);
          return;
        }
        if (r.status === 404) {
          const m = fileUrl ? normalizeManifest({ file_url: fileUrl, unbranded }) : null;
          setManifest(m);
          setState(m ? "ready" : "missing");
          return;
        }
        if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
        const m = normalizeManifest({ ...body, unbranded: unbranded || body.unbranded });
        setManifest(m);
        setState(m ? "ready" : "missing");
      })
      .catch((e) => {
        if (cancelled) return;
        const m = fileUrl ? normalizeManifest({ file_url: fileUrl, unbranded }) : null;
        if (m) {
          setManifest(m);
          setState("ready");
        } else {
          setError(String(e.message || e));
          setState("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [visitId, fileUrl, unbranded, enabled, nonce]);

  const unlock = useCallback(
    async (password) => {
      const r = await fetch(`${BASE}/api/public/visits/${encodeURIComponent(visitId)}/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.token) {
        throw new Error(r.status === 429 ? "Too many tries. Wait a minute." : "That password didn't work.");
      }
      try {
        window.sessionStorage.setItem(tokenKey(visitId), body.token);
      } catch {
        /* private mode: token only lives in memory via the refetch below */
      }
      setNonce((n) => n + 1);
    },
    [visitId]
  );

  return { state, manifest, error, unlock };
}
